export function parseMapUrl(raw) {
    if (!raw)
        return null;
    let u;
    try {
        u = new URL(raw.trim());
    }
    catch {
        return null;
    }
    const host = u.hostname.toLowerCase();
    if (host.includes('google.') || host.includes('goo.gl')) {
        return parseGoogle(u);
    }
    if (host.includes('amap.com') || host.includes('gaode.com')) {
        return parseAmap(u);
    }
    return null;
}
function parseGoogle(u) {
    let title = null;
    let lat = null;
    let lng = null;
    // /maps/place/<name>/  — take the name; `@lat,lng` after it is the viewport,
    // the actual pin is in the data= fragment (!3d!4d below).
    const place = u.pathname.match(/\/place\/([^/@?]+)/);
    if (place)
        title = safeDecode(place[1]).replace(/\+/g, ' ');
    // Actual pin: !3d<lat>!4d<lng> in the data= fragment (preferred over viewport).
    const pin = (u.pathname + u.search + u.hash).match(/!3d(-?\d+\.\d+)!4d(-?\d+\.\d+)/);
    if (pin) {
        lat = parseFloat(pin[1]);
        lng = parseFloat(pin[2]);
    }
    // Viewport / fallback: @lat,lng in pathname
    if (lat == null) {
        const at = u.pathname.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
        if (at) {
            lat = parseFloat(at[1]);
            lng = parseFloat(at[2]);
        }
    }
    // ll= / center= as a last coord fallback
    const ll = u.searchParams.get('ll') ?? u.searchParams.get('center');
    if (lat == null && ll) {
        const m = ll.split(',').map(parseFloat);
        if (!isNaN(m[0]) && !isNaN(m[1])) {
            lat = m[0];
            lng = m[1];
        }
    }
    // q= / query= — coords if numeric; otherwise place name IF it doesn't look
    // like a Google-internal opaque token (those should never be a visible title).
    const q = u.searchParams.get('q') ?? u.searchParams.get('query');
    if (q) {
        const coord = q.match(/^(-?\d+\.\d+),(-?\d+\.\d+)$/);
        if (coord && lat == null) {
            lat = parseFloat(coord[1]);
            lng = parseFloat(coord[2]);
        }
        else if (!title && !isGoogleInternalToken(q)) {
            title = safeDecode(q).replace(/\+/g, ' ');
        }
    }
    return { title, lat, lng, source: 'google' };
}
function isGoogleInternalToken(s) {
    if (s.length > 20 && /^[A-Za-z0-9_-]+$/.test(s))
        return true; // long base64-url blob
    if (/^(ChIJ|Eg[A-Z]|0x[0-9a-f]+:0x[0-9a-f]+)/.test(s))
        return true; // place IDs / CIDs
    return false;
}
function safeDecode(s) {
    try {
        return decodeURIComponent(s);
    }
    catch {
        return s;
    }
}
function parseAmap(u) {
    let title = null;
    let lat = null;
    let lng = null;
    // position=lng,lat
    const pos = u.searchParams.get('position') ?? u.searchParams.get('center');
    if (pos) {
        const parts = pos.split(',').map(parseFloat);
        if (!isNaN(parts[0]) && !isNaN(parts[1])) {
            lng = parts[0];
            lat = parts[1];
        }
    }
    // name
    const name = u.searchParams.get('name') ?? u.searchParams.get('keywords') ?? u.searchParams.get('query');
    if (name)
        title = decodeURIComponent(name).replace(/\+/g, ' ');
    return { title, lat, lng, source: 'amap' };
}
/** Build a "open in map app" URL. `prefer` defaults to google. */
export function mapOpenUrl(opts, prefer = 'google') {
    const { lat, lng, name } = opts;
    const hasCoord = lat != null && lng != null && !isNaN(lat) && !isNaN(lng);
    if (!hasCoord && !name)
        return null;
    if (prefer === 'amap') {
        if (hasCoord) {
            const q = new URLSearchParams();
            q.set('position', `${lng},${lat}`);
            if (name)
                q.set('name', name);
            return `https://uri.amap.com/marker?${q.toString()}`;
        }
        return `https://uri.amap.com/search?keyword=${encodeURIComponent(name)}`;
    }
    // google
    if (hasCoord && name) {
        return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(name)}&query_place_id=&center=${lat},${lng}`;
    }
    if (hasCoord) {
        return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
    }
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(name)}`;
}
